import { Head, router } from "@inertiajs/react"
import { Building2, ChevronRight, MapPin, Shield, Users } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"

interface Organization {
  id: number
  name: string
  slug: string
  partner: boolean
  parent_org_id: number | null
  logo_url: string | null
  facilities_count: number
  users_count: number
  role: string
  member: boolean
}

interface Props {
  organizations: Organization[]
}

function roleBadge(org: Organization) {
  if (!org.member) {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 text-xs text-amber-600 dark:text-amber-400"
      >
        <Shield className="mr-1 h-3 w-3" /> superadmin
      </Badge>
    )
  }
  const colorByRole: Record<string, string> = {
    admin: "border-blue-500/40 text-blue-600 dark:text-blue-400",
    viewer: "border-slate-400/40 text-slate-600 dark:text-slate-400",
    member: "border-slate-400/40 text-slate-600 dark:text-slate-400",
  }
  return (
    <Badge
      variant="outline"
      className={`text-xs ${colorByRole[org.role] ?? "border-slate-400/40"}`}
    >
      {org.role}
    </Badge>
  )
}

function OrgCard({ org }: { org: Organization }) {
  return (
    <Card
      key={org.id}
      onClick={() => router.visit(`/${org.slug}`)}
      className="group hover:border-primary/50 cursor-pointer overflow-hidden transition-all hover:shadow-md"
    >
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {org.logo_url ? (
              <div className="bg-muted flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg">
                <img
                  src={org.logo_url}
                  alt={`${org.name} logo`}
                  className="h-8 w-auto max-w-full object-contain"
                />
              </div>
            ) : (
              <div className="bg-primary/10 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
                <Building2 className="text-primary h-5 w-5" />
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-semibold">{org.name}</p>
              <p className="text-muted-foreground truncate font-mono text-xs">
                /{org.slug}
              </p>
            </div>
          </div>
          <ChevronRight className="text-muted-foreground h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {roleBadge(org)}
          {org.partner && (
            <Badge
              variant="outline"
              className="border-purple-500/40 text-xs text-purple-600 dark:text-purple-400"
            >
              partner
            </Badge>
          )}
        </div>

        <div className="text-muted-foreground flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1">
            <MapPin className="h-3.5 w-3.5" />
            {org.facilities_count} facility{org.facilities_count !== 1 && "s"}
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {org.users_count} {org.users_count === 1 ? "user" : "users"}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

export default function Organizations({ organizations }: Props) {
  const total = organizations.length
  const partners = organizations.filter((o) => o.partner)
  const partnerCount = partners.length
  const partnerIds = new Set(partners.map((p) => p.id))

  // A customer is any org whose parent is a partner shown in this list. Anything
  // that's neither a partner nor a listed customer renders in the flat grid.
  const customersByPartner = new Map<number, Organization[]>()
  const claimed = new Set<number>()
  for (const partner of partners) {
    const customers = organizations.filter(
      (o) => o.parent_org_id === partner.id,
    )
    customers.forEach((c) => claimed.add(c.id))
    customersByPartner.set(partner.id, customers)
  }
  const standalone = organizations.filter(
    (o) => !partnerIds.has(o.id) && !claimed.has(o.id),
  )

  return (
    <AppLayout>
      <Head title="Organizations" />

      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Organizations</h1>
            <p className="text-muted-foreground text-sm">
              {total} organization{total !== 1 && "s"}
              {partnerCount > 0 && ` · ${partnerCount} partner`}
            </p>
          </div>
        </div>

        {organizations.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center">
            No organizations yet.
          </p>
        ) : (
          <div className="space-y-8">
            {partners.map((partner) => {
              const customers = customersByPartner.get(partner.id) ?? []
              return (
                <div key={partner.id} className="space-y-3">
                  <OrgCard org={partner} />
                  {customers.length > 0 && (
                    <div className="border-border/60 ml-4 space-y-3 border-l pl-4">
                      <p className="text-muted-foreground text-xs font-medium">
                        Customers
                      </p>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {customers.map((customer) => (
                          <OrgCard key={customer.id} org={customer} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}

            {standalone.length > 0 && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {standalone.map((org) => (
                  <OrgCard key={org.id} org={org} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
